import { Injectable, inject } from '@angular/core';
import { FeatureEntity } from '@gishub/domain';
import { UbigeoProvider } from '../providers/ubigeo-provider';
import { buildLocalInterventions } from '../data/local-interventions';

function normalize(value: string): string {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase();
}

export interface LocalFilter {
  region?: string | null;
  provincia?: string | null;
  distrito?: string | null;
}

/**
 * Devuelve true si el feature fue inyectado desde los datos locales
 * (no proviene del MapServer/6 de ArcGIS).
 */
export function isLocalFeature(feature: FeatureEntity): boolean {
  return feature.attributes['_local'] === true;
}

export function filterLocalByNames(features: FeatureEntity[], filter: LocalFilter): FeatureEntity[] {
  const nRegion = filter.region ? normalize(filter.region) : null;
  const nProv = filter.provincia ? normalize(filter.provincia) : null;
  const nDist = filter.distrito ? normalize(filter.distrito) : null;

  return features.filter(f => {
    const a = f.attributes;
    const region = normalize(a['region'] ?? a['departamento'] ?? '');
    const prov = normalize(a['provincia'] ?? '');
    const dist = normalize(a['distrito'] ?? '');

    if (nRegion && region && region !== nRegion) return false;
    if (nProv && prov && prov !== nProv) return false;
    if (nDist && dist && dist !== nDist) return false;
    return true;
  });
}

@Injectable({ providedIn: 'root' })
export class LocalInterventionService {
  private readonly _ubigeo = inject(UbigeoProvider);

  getLocalFeatures(): FeatureEntity[] {
    return buildLocalInterventions().map(f => ({
      attributes: { ...f.attributes },
      geometry: f.geometry,
    }));
  }

  /** Prepende las inversiones locales (filtradas por el ámbito ubigeo actual) a las de ArcGIS. */
  mergeInterventions(features: FeatureEntity[]): FeatureEntity[] {
    const local = this.filterLocalByUbigeo(this.getLocalFeatures());
    return [...local, ...features];
  }

  filterLocalByUbigeo(features: FeatureEntity[]): FeatureEntity[] {
    const u = this._ubigeo.state();
    return filterLocalByNames(features, {
      region: u.selectedDepartmentName || null,
      provincia: u.selectedProvinceName && u.selectedProvinceName !== 'Toda la Región' ? u.selectedProvinceName : null,
      distrito: u.selectedDistrictName && u.selectedDistrictName !== 'Todos los Distritos' ? u.selectedDistrictName : null,
    });
  }
}